/**
 * A pesquisa de alunos do painel da Escola (o botão do cabeçalho e a acção da paleta ⌘K):
 * uma janela com o campo em cima e os resultados por baixo, que se percorrem com ↑ ↓ e se
 * abrem com Enter. Pesquisa pelo nome a partir de 2 letras (`GET {searchUrl}?q=&limite=20`).
 */
import { useEffect, useRef, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { Icon } from "@iconify/react";
import { ChevronRight, Loader2, Search, X } from "lucide-react";
import { Avatar, Botao, BotaoIcone, Esqueleto, Janela, Selo, useValorAtrasado } from "@/Logigate/ui";
import { cn } from "@/lib/utils";
import { track } from "@/utils/px";
import req from "@/utils/req";
import { useBasePath } from "@/Escola/hooks/useBasePath";

interface Aluno {
    id: number;
    nome: string;
    foto_link?: string;
    turma?: string;
    classe?: string;
    estado?: number;
    idade?: string | number;
    /** Ainda não vem do `pesquisarAlunos`; aparece se um dia vier. */
    numero?: string | number;
    numero_formatado?: string;
}

interface DialogPesquisarAlunosProps {
    opened: boolean;
    onClose: () => void;
    searchUrl?: string;
}

/** Os estados do modelo `Alunos.php` (`ESTADO_ACTIVO = 0` … `ESTADO_EXPULSO = 5`). */
const ESTADOS: Record<number, { cor: string; rotulo: string }> = {
    0: { cor: "green", rotulo: "Activo" },
    1: { cor: "orange", rotulo: "Desistente" },
    2: { cor: "gray", rotulo: "Trancado" },
    3: { cor: "blue", rotulo: "Finalizado" },
    4: { cor: "yellow", rotulo: "Transferido" },
    5: { cor: "red", rotulo: "Expulso" },
};

const MINIMO = 2;

export function DialogPesquisarAlunos({ opened, onClose, searchUrl = "/alunos/pesquisar" }: DialogPesquisarAlunosProps) {
    const basePath = useBasePath();
    const [search, setSearch] = useState("");
    const debouncedSearch = useValorAtrasado(search, 500);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [alunos, setAlunos] = useState<Aluno[]>([]);
    /** O termo dos resultados que estão à vista. */
    const [termoMostrado, setTermoMostrado] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [tentativa, setTentativa] = useState(0);
    const ultimoPedido = useRef(0);
    const lista = useRef<HTMLUListElement>(null);

    // Cada abertura começa do zero (limpa-se também ao fechar, para o termo atrasado não
    // disparar uma pesquisa antiga quando se volta a abrir).
    useEffect(() => {
        setSearch("");
        setAlunos([]);
        setTermoMostrado("");
        setErro(null);
        setLoading(false);
        setSelectedIndex(0);
    }, [opened]);

    useEffect(() => {
        const termo = debouncedSearch.trim();
        // Uma resposta que chega depois de outra pesquisa já não conta.
        const pedido = ++ultimoPedido.current;

        if (!opened || termo.length < MINIMO) {
            setAlunos([]);
            setTermoMostrado("");
            setErro(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setErro(null);
        req()
            .get(basePath(searchUrl), { params: { q: termo, limite: 20 } })
            .then((response) => {
                if (pedido !== ultimoPedido.current) {
                    return;
                }
                if (response.data?.estado === "ok") {
                    setAlunos(response.data.items || []);
                    setTermoMostrado(termo);
                    setSelectedIndex(0);
                } else {
                    setAlunos([]);
                    setErro(response.data?.texto || "Não foi possível pesquisar os alunos.");
                }
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, opened, tentativa]);

    // O aluno escolhido com o teclado fica sempre à vista.
    useEffect(() => {
        lista.current?.querySelector<HTMLElement>(`[data-indice="${selectedIndex}"]`)?.scrollIntoView({ block: "nearest" });
    }, [selectedIndex]);

    const linkAluno = (aluno: Aluno) => basePath(`/alunos/${aluno.id}`);

    const abrir = (aluno: Aluno) => {
        onClose();
        router.visit(linkAluno(aluno));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex((prev) => Math.min(prev + 1, alunos.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter" && alunos[selectedIndex]) {
            e.preventDefault();
            abrir(alunos[selectedIndex]);
        }
    };

    const fechar = () => {
        track("click", "escola_pesquisar_alunos_fechar", {});
        onClose();
    };

    const curto = search.trim().length < MINIMO;
    const aEspera = !curto && (loading || debouncedSearch !== search) && alunos.length === 0 && !erro;

    return (
        <Janela
            aberta={opened}
            onFechar={onClose}
            confirmarAoFechar={false}
            largura="lg"
            // O X da janela dá lugar ao da barra de pesquisa (que regista o fecho).
            className="[&_[aria-label=Fechar]]:hidden"
            corpoClassName="p-0"
        >
            <div data-nao-suja className="sticky top-0 z-[5] rounded-t-2xl border-b border-zinc-100 bg-white/95 px-3 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/85 sm:rounded-t-xl sm:px-4 dark:border-zinc-800 dark:bg-zinc-900/95">
                <div className="flex items-center gap-2">
                    <div className="relative min-w-0 flex-1">
                        <Search className="pointer-events-none absolute left-3.5 top-1/2 size-5 -translate-y-1/2 text-zinc-400" aria-hidden />
                        <input
                            type="search"
                            data-autofocus
                            value={search}
                            onChange={(e) => setSearch(e.currentTarget.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Nome do aluno…"
                            aria-label="Pesquisar alunos pelo nome"
                            role="combobox"
                            aria-expanded={alunos.length > 0}
                            aria-controls="resultados-pesquisa-alunos"
                            aria-activedescendant={alunos[selectedIndex] ? `aluno-pesquisa-${alunos[selectedIndex].id}` : undefined}
                            autoComplete="off"
                            spellCheck={false}
                            className={cn(
                                "h-12 w-full rounded-xl border border-zinc-200 bg-zinc-50 pl-11 pr-11 text-base text-zinc-900 outline-none transition-colors",
                                "placeholder:text-zinc-400 focus:border-portal focus:bg-white focus:ring-2 focus:ring-portal/20",
                                "[&::-webkit-search-cancel-button]:hidden",
                                "dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:focus:bg-zinc-950",
                            )}
                        />
                        <span className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
                            {loading ? (
                                <Loader2 className="mr-2 size-5 animate-spin text-portal dark:text-portal-claro" aria-label="A pesquisar" />
                            ) : search.length > 0 ? (
                                <BotaoIcone
                                    rotulo="Limpar a pesquisa"
                                    className="size-10"
                                    onClick={() => {
                                        track("click", "escola_pesquisar_alunos_limpar_pesquisa", {});
                                        setSearch("");
                                    }}
                                >
                                    <X className="size-4" />
                                </BotaoIcone>
                            ) : null}
                        </span>
                    </div>
                    <BotaoIcone rotulo="Fechar a pesquisa" tamanho="lg" className="size-11 sm:size-10" onClick={fechar}>
                        <X className="size-5" />
                    </BotaoIcone>
                </div>
            </div>

            <div className="min-h-[18rem] p-2 sm:min-h-[22rem]">
                {curto ? (
                    <Estado
                        icone="mdi:account-search-outline"
                        titulo="Procura um aluno"
                        texto={`Escreve pelo menos ${MINIMO} letras do nome para começar.`}
                    />
                ) : erro ? (
                    <Estado
                        icone="mdi:wifi-alert"
                        cor="red"
                        titulo="Não foi possível pesquisar"
                        texto={erro}
                        accao={<Botao variante="neutro" tamanho="sm" className="h-11 sm:h-8" onClick={() => setTentativa((t) => t + 1)}>Tentar de novo</Botao>}
                    />
                ) : aEspera ? (
                    <div className="space-y-1 p-1" aria-busy>
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center gap-3 rounded-xl px-3 py-3">
                                <Esqueleto className="size-11 shrink-0 rounded-full" />
                                <div className="min-w-0 flex-1 space-y-2">
                                    <Esqueleto className="h-3.5 w-1/2" />
                                    <Esqueleto className="h-3 w-1/3" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : alunos.length === 0 ? (
                    <Estado
                        icone="mdi:account-off-outline"
                        titulo="Nenhum aluno encontrado"
                        texto={<>Não há alunos com «{termoMostrado || search.trim()}» no nome.</>}
                    />
                ) : (
                    <ul ref={lista} id="resultados-pesquisa-alunos" role="listbox" aria-label="Alunos encontrados" className={cn("space-y-0.5 transition-opacity", loading && "opacity-60")}>
                        {alunos.map((aluno, index) => {
                            const activo = index === selectedIndex;
                            const estado = aluno.estado === undefined || aluno.estado === null ? undefined : ESTADOS[aluno.estado];
                            const turma = aluno.classe && aluno.turma ? `${aluno.classe} · ${aluno.turma}` : aluno.classe || aluno.turma;
                            const numero = aluno.numero_formatado ?? aluno.numero;
                            const idade = aluno.idade !== undefined && aluno.idade !== null && aluno.idade !== ""
                                ? `${aluno.idade}${typeof aluno.idade === "number" ? " anos" : ""}`
                                : null;
                            return (
                                <li key={aluno.id} id={`aluno-pesquisa-${aluno.id}`} role="option" aria-selected={activo} data-indice={index}>
                                    <Link
                                        href={linkAluno(aluno)}
                                        onClick={onClose}
                                        onMouseMove={() => !activo && setSelectedIndex(index)}
                                        className={cn(
                                            "flex min-h-16 items-center gap-3 rounded-xl px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-portal/40",
                                            activo
                                                ? "bg-portal-suave dark:bg-portal-claro/10"
                                                : "hover:bg-zinc-50 dark:hover:bg-zinc-800/60",
                                        )}
                                    >
                                        <Avatar src={aluno.foto_link} nome={aluno.nome} tamanho="md" className="size-11" />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex min-w-0 items-center gap-2">
                                                <p className={cn("truncate text-sm font-semibold", activo ? "text-portal-texto dark:text-portal-claro" : "text-zinc-900 dark:text-zinc-100")}>
                                                    {aluno.nome}
                                                </p>
                                                {estado && <Selo cor={estado.cor} tamanho="xs" className="hidden sm:inline-flex">{estado.rotulo}</Selo>}
                                            </div>
                                            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                                                {numero !== undefined && numero !== null && numero !== "" && <span className="tabular-nums">Nº {numero}</span>}
                                                {turma && (
                                                    <span className="inline-flex min-w-0 items-center gap-1">
                                                        <Icon icon="mdi:google-classroom" className="size-3.5 shrink-0" aria-hidden />
                                                        <span className="truncate">{turma}</span>
                                                    </span>
                                                )}
                                                {idade && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Icon icon="mdi:cake-variant-outline" className="size-3.5 shrink-0" aria-hidden />
                                                        {idade}
                                                    </span>
                                                )}
                                                {estado && <Selo cor={estado.cor} tamanho="xs" className="sm:hidden">{estado.rotulo}</Selo>}
                                            </div>
                                        </div>
                                        <ChevronRight className={cn("size-4 shrink-0 transition-colors", activo ? "text-portal dark:text-portal-claro" : "text-zinc-300 dark:text-zinc-600")} aria-hidden />
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-zinc-100 px-4 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
                <span aria-live="polite">
                    {!curto && !erro && alunos.length > 0
                        ? `${alunos.length} ${alunos.length === 1 ? "aluno encontrado" : "alunos encontrados"}`
                        : "Pesquisa pelo nome"}
                </span>
                <span className="hidden items-center gap-3 sm:flex" aria-hidden>
                    <span className="inline-flex items-center gap-1"><Tecla>↑</Tecla><Tecla>↓</Tecla> navegar</span>
                    <span className="inline-flex items-center gap-1"><Tecla>Enter</Tecla> abrir</span>
                    <span className="inline-flex items-center gap-1"><Tecla>Esc</Tecla> fechar</span>
                </span>
            </div>
        </Janela>
    );
}

function Tecla({ children }: { children: React.ReactNode }) {
    return (
        <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-zinc-200 bg-white px-1 font-sans text-[10px] font-medium text-zinc-600 shadow-[0_1px_0] shadow-zinc-200 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:shadow-zinc-900">
            {children}
        </kbd>
    );
}

function Estado({ icone, cor = "gray", titulo, texto, accao }: {
    icone: string;
    cor?: "gray" | "red";
    titulo: React.ReactNode;
    texto: React.ReactNode;
    accao?: React.ReactNode;
}) {
    return (
        <div className="flex min-h-[17rem] flex-col items-center justify-center px-6 py-10 text-center sm:min-h-[21rem]">
            <span
                className={cn(
                    "flex size-14 items-center justify-center rounded-full",
                    cor === "red"
                        ? "bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400"
                        : "bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500",
                )}
            >
                <Icon icon={icone} className="size-7" />
            </span>
            <p className="mt-4 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{titulo}</p>
            <p className="mt-1 max-w-xs text-[13px] leading-relaxed text-zinc-500 dark:text-zinc-400">{texto}</p>
            {accao && <div className="mt-4">{accao}</div>}
        </div>
    );
}
